import { FC, useState, useEffect, useCallback } from 'react';
import { AuthenticationResult } from '@azure/msal-browser';
import { loginRequest, getMsalInstance } from '@/config/authConfig';
import { useAuthContext } from '@/contexts/AuthContext';

interface MicrosoftLoginButtonProps {
  onSuccess: (result: { created: boolean; needsProfileCompletion: boolean }) => void;
  onError: (error: string) => void;
}

const MicrosoftLoginButton: FC<MicrosoftLoginButtonProps> = ({ onSuccess, onError }) => {
  const { loginWithMicrosoft } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);

  // Handle redirect response when returning from Azure AD
  const handleRedirectResponse = useCallback(async (response: AuthenticationResult) => {
    if (!response.idToken) {
      onError('Token nao recebido do Microsoft');
      return;
    }

    setIsLoading(true);
    try {
      const result = await loginWithMicrosoft(response.idToken);
      onSuccess(result);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } }; message?: string };
      const message =
        error.response?.data?.error ||
        error.message ||
        'Erro ao fazer login com Microsoft';
      onError(message);
    } finally {
      setIsLoading(false);
    }
  }, [loginWithMicrosoft, onSuccess, onError]);

  // On mount, check if we're returning from a redirect
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const instance = await getMsalInstance();
        if (!instance || cancelled) return;

        const response = await instance.handleRedirectPromise();
        if (response && !cancelled) {
          await handleRedirectResponse(response);
        }
      } catch (err: unknown) {
        if (cancelled) return;
        const error = err as { errorCode?: string; message?: string };
        if (error.errorCode !== 'interaction_in_progress') {
          onError(error.message || 'Erro ao processar resposta do Microsoft');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [handleRedirectResponse, onError]);

  const handleClick = async () => {
    setIsLoading(true);
    try {
      const instance = await getMsalInstance();
      if (!instance) {
        onError('SSO Microsoft nao configurado');
        setIsLoading(false);
        return;
      }

      // Redirect flow — navigates away from the page
      await instance.loginRedirect(loginRequest);
    } catch (err: unknown) {
      const error = err as { errorCode?: string; message?: string };

      if (error.errorCode === 'interaction_in_progress') {
        setIsLoading(false);
        return;
      }

      const message = error.message || 'Erro ao fazer login com Microsoft';
      onError(message);
      setIsLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading}
      className="group relative flex w-full items-center justify-center gap-3 rounded-lg bg-[#FCB428] px-3 py-3.5 text-sm font-semibold text-[#003046] hover:bg-[#FFC94D] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FCB428] disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <svg className="h-5 w-5" viewBox="0 0 21 21" fill="none">
        <rect x="1" y="1" width="9" height="9" fill="#F25022" />
        <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
        <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
        <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
      </svg>
      {isLoading ? 'Entrando...' : 'Entrar com conta corporativa'}
    </button>
  );
};

export default MicrosoftLoginButton;
